// src/components/game-supergirls/GameSupergirls.js
import React, { useState, useEffect, useRef } from "react";
import api from "./api/axios";
import "./GameSupergirls.css";

import ProfileHeader from "../header/ProfileHeader";
import QuizScreen from "./QuizScreen";
import RewardScreen from "./RewardScreen";
import AlreadyPlayedScreen from "./AlreadyPlayedScreen";
import LoadingBar from "../LoadingBar";

import RulesScreen from "./RulesScreen";
import CheckRewardLimit from "./CheckRewardLimit";
import GameEndedScreen from "./GameEndedScreen";

// ─────────────────────────────────────
// GameContent: ส่วน Logic เกม (เหมือนเดิม)
// ─────────────────────────────────────

function GameContent({ gameMe }) {
  const userId = gameMe?.line_id;
  const [questionSet, setQuestionSet] = useState(null);
  const [question, setQuestion] = useState(null);
  const [selectedOption, setSelectedOption] = useState(null);
  const [submitResult, setSubmitResult] = useState(null);
  const [alreadyPlayed, setAlreadyPlayed] = useState(false);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const loadQuestions = async () => {
      setLoading(true);
      try {
        const setRes = await api.get("/game-questions/current");
        const setData = setRes.data;
        setQuestionSet(setData);
        console.log(setData);

        if (setData?.id) {
          const playedRes = await api.get(
            `/game-questions/check-played/${userId}/${setData.id}`
          );
          if (playedRes.data.played) {
            setAlreadyPlayed(true);
            setLoading(false);
            return;
          }
          const qRes = await api.get(`/game-questions/${setData.id}`);
          setQuestion(qRes.data);
        }
      } catch (err) {
        console.error(err);
      }
      setLoading(false);
    };
    loadQuestions();
  }, [userId]);

  const submitAnswer = async () => {
    setLoading(true);
    try {
      const payload = {
        lineId: userId,
        questionSetId: questionSet.id,
        answers: [{ question_id: question.id, answer_option: selectedOption }],
      };
      const res = await api.post("/game-plays/submit", payload);

      if (res.data?.alreadyPlayed) {
        setSubmitResult({ alreadyPlayed: true });
        setLoading(false);
        return;
      }
      setSubmitResult(res.data);
    } catch (err) {
      console.error("Submit error:", err);
    }
    setLoading(false);
  };

  if (loading) return <LoadingBar loading={loading} />;

  if (submitResult?.alreadyPlayed || alreadyPlayed)
    return <AlreadyPlayedScreen />;
  if (submitResult)
    return <RewardScreen result={submitResult} lineUserId={userId} />;

  return (
    <QuizScreen
      question={question}
      selectedOptionId={selectedOption}
      onAnswer={setSelectedOption}
      onSubmit={submitAnswer}
      isLastQuestion={true}
      currentIndex={0}
      totalQuestions={1}
    />
  );
}

// ─────────────────────────────────────
// Wrapper หลัก (จุดที่แก้ไข Logic เช็ค available)
// ─────────────────────────────────────
export default function GameSupergirls() {
  const [loading, setLoading] = useState(true);
  const [gameMe, setGameMe] = useState(null);
  const [hasStarted, setHasStarted] = useState(false);
  const [isGameEnded, setIsGameEnded] = useState(false);

  // State เช็คว่าของหมดหรือยัง
  const [isStockFull, setIsStockFull] = useState(false);

  const scrollRef = useRef(null);
  const userId = localStorage.getItem("userId") ?? "";

  useEffect(() => {
    const initData = async () => {
      setLoading(true);
      try {
        // ✅ เช็ควันหมดกิจกรรมก่อนทุกอย่าง
        const now = new Date();
        const endDate = new Date("2025-12-25T18:00:00.000Z");

        if (now > endDate) {
          setIsGameEnded(true);
          setLoading(false);
          return;
        }
        // ยิง Parallel Request: โหลดข้อมูล User และ เช็ค Stock
        const [meRes, stockRes] = await Promise.allSettled([
          api.get(`/game-me?lineId=${userId}&name=`),
          api.get(`/game-rewards/check-available`, {
            headers: {
              "x-api-key": "RE1LVC1CaWdDLUludGVybmFs",
            },
          }),
        ]);

        // 1. จัดการข้อมูล User
        if (meRes.status === "fulfilled") {
          setGameMe(meRes.value.data?.player);
        }

        // 2. ✅ จัดการข้อมูล Stock (ตาม JSON ที่ส่งมา)
        if (stockRes.status === "fulfilled") {
          const result = stockRes.value.data;
          console.log("Check Available Response:", result);

          // เงื่อนไข: ถ้า available เป็น false = ของหมด
          // JSON: { "available": false, "message": "..." }
          if (result && result.available === false) {
            console.warn("⛔ Reward Limit Reached:", result.message);
            setIsStockFull(true); // ตั้งค่าให้แสดงหน้า CheckRewardLimit
          }
        } else {
          console.error("Check Stock Failed:", stockRes.reason);
        }
      } catch (err) {
        console.error("Init Error:", err);
      }
      setLoading(false);
    };

    initData();
  }, [userId]);

  if (loading) {
    return <LoadingBar loading={loading} label="Loading..." />;
  }

  // ✅ กิจกรรมจบแล้ว
  if (isGameEnded) {
    return (
      <div className="h-screen flex flex-col bg-secondary-background-gray">
        <ProfileHeader className="bg-primary" />
        <div className="flex-grow overflow-y-auto">
          <GameEndedScreen />
        </div>
      </div>
    );
  }
  // ✅ ถ้าของหมด (isStockFull = true) แสดงหน้า CheckRewardLimit ทันที
  // และจะไม่ Render ส่วน RulesScreen หรือ GameContent เลย
  if (isStockFull) {
    return (
      <div className="w-full flex flex-col gap-y-4">
        <div className="h-screen flex flex-col bg-secondary-background-gray">
          <ProfileHeader className="bg-primary" />
          <div className="flex-grow overflow-y-auto">
            <CheckRewardLimit />
          </div>
        </div>
      </div>
    );
  }

  if (!gameMe) {
    return console.error("❌ ไม่พบข้อมูลผู้เล่น");
  }

  return (
    <div className="w-full flex flex-col gap-y-4">
      <div className="h-screen flex flex-col bg-secondary-background-gray">
        <ProfileHeader className="bg-primary" />

        <div className="flex-grow overflow-y-auto" ref={scrollRef}>
          {!hasStarted && !isGameEnded ? (
            <RulesScreen onStart={() => setHasStarted(true)} />
          ) : (
            <GameContent gameMe={gameMe} />
          )}
        </div>
      </div>
    </div>
  );
}
